import 'polyfills/promiseWithResolvers';
import 'components/anvilFormFillable/pdfWorker';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import React, {
  CSSProperties,
  FC,
  useLayoutEffect,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Document, Page } from 'react-pdf';
import {
  FieldComponentProps,
  FieldType,
  AnvilForm,
} from 'components/anvilFormFillable/fields/types';
import RadioGroup from 'components/anvilFormFillable/fields/Radio';
import CheckboxGroup from 'components/anvilFormFillable/fields/CheckboxGroup';
import Checkbox from 'components/anvilFormFillable/fields/Checkbox';
import ShortText from 'components/anvilFormFillable/fields/ShortText';
import Date from 'components/anvilFormFillable/fields/Date';
import SignatureTimestamp from 'components/anvilFormFillable/fields/SignatureTimestamp';
import Button from 'components/common/button';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import clsx from 'clsx';
import {
  Controller,
  FormProvider,
  useForm,
} from 'react-hook-form';
import Wrapper from 'components/common/Wrapper';
import isNil from 'lodash/isNil';
import SignatureStep from 'components/anvilFormFillable/Step';
import {
  formatDate,
  formatPhone,
  formatSignatureTimestamp,
  formatWithAffix,
  getAddressString,
  isEmptyString,
  phoneValidation,
  validateDate,
  validateEmail,
} from 'utils/helper';
import { NormalizedFormTemplateMetaData } from 'types/normalizedFormTemplate';
import head from 'lodash/head';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import Spinner from 'components/spinner/Spinner';
import keyBy from 'lodash/keyBy';
import MuiCheckbox from '@mui/material/Checkbox';
import NumberInput from 'components/anvilFormFillable/fields/NumberInput';
import PhoneInput from 'components/anvilFormFillable/fields/Phone';
import SignatureInput from 'components/anvilFormFillable/fields/SignatureInput';
import TaxpayerNumber from 'components/anvilFormFillable/fields/TaxpayerNumber';
import USAddress from 'components/anvilFormFillable/fields/USAddress';
import SignatureBox from 'components/anvilFormFillable/fields/SignatureBox';
import FormFieldWizardLabel from 'components/anvilFormFillable/FormFieldWizardLabel';
import {
  getFieldHorizontalAlignment,
  getFieldTextAlignment,
  getFieldVerticalAlignment,
  getStableKey,
  getStyleFromRect,
  isFormFieldRequired,
} from 'components/anvilFormFillable/utils';
import some from 'lodash/some';
import CurrencyTextField from 'components/anvilFormFillable/fields/CurrencyTextField';
import ToolTip from 'components/common/tooltip';
import LongText from 'components/anvilFormFillable/fields/LongText';
import SignatureCanvas from 'react-signature-canvas';
import { AddressDTO } from 'artifacts/dtos/addressDTO';
import { useOrgTimeZone } from 'hooks/useOrgTimeZone';
import { getOrgNow } from 'utils/dayjs';
import {
  applyGlobalSignatureToFields,
  getFieldStateKey,
  getPreservedGlobalPrepValues,
} from 'utils/signaturePrep';

const animationClass = 'transition-all duration-300 ease-in-out transform';

function hasFieldByType(data: NormalizedFormTemplateMetaData[], fieldName: FieldType): boolean {
  return some(flatMap(data, 'fields'), { type: fieldName });
}

const formatterByType: Record<string, (val: string) => string> = {
  phone: (val) => formatPhone(val.slice(1)),
  dollar: (val) => formatWithAffix(val, { prefix: '$' }),
  percent: (val) => formatWithAffix(val, { suffix: '%' }),
};

const validatorsByType: Record<string, (
  val?: string | boolean | AddressDTO | string[] | null
) => boolean> = {
  email: (val) => validateEmail(val as string),
  phone: (val) => phoneValidation(val as string),
  checkbox: (val) => val === true,
  checkboxGroup: (val) => isArray(val) && val.length > 0,
  date: (val) => validateDate(val as string),
  signatureTimestamp: (val) => !isEmptyString(val as string),
  ssn: (val) => (val as string)?.length === 11,
  ein: (val) => (val as string)?.length === 10,
  usAddress: (val) => getAddressString(val as AddressDTO) !== '--',
};

const componentMap: Record<Partial<FieldType>, FC<FieldComponentProps>> = {
  radioGroup: RadioGroup,
  checkboxGroup: CheckboxGroup,
  checkbox: Checkbox,
  fullName: ShortText,
  phone: PhoneInput,
  email: ShortText,
  ssn: TaxpayerNumber,
  ein: TaxpayerNumber,
  usAddress: USAddress,
  longText: LongText,
  shortText: ShortText,
  signature: SignatureInput,
  initial: SignatureInput,
  date: Date,
  signatureDate: Date,
  signatureTimestamp: SignatureTimestamp,
  number: NumberInput,
  integer: NumberInput,
  dollar: CurrencyTextField,
  percent: NumberInput,
};

const signPreparationFields = [
  {
    title: 'Draw or type your signature',
    name: 'signature',
    description: 'Your signature will appear on all documents where you need to sign.',
    placeholder: 'Ex. John Deo',
  },
  {
    title: 'Initials',
    name: 'initial',
    description: 'Your initials will appear on all documents where you need to initial.',
    placeholder: 'Ex. JD',
  },
  {
    title: 'Sign all documents',
    name: 'consent',
    info: 'Review and sign the documents below.',
    description: 'I consent to using electronic signatures and this website storing my given information according to their privacy policy.',
  },
  {
    title: 'Review all documents',
    name: 'consentWithoutSigning',
    info: 'Review the documents below before continuing.',
    description: 'I consent to this website storing my given information according to their privacy policy.',
  },
];

// The dynamic Anvil fields stored in RHF can be deeply recursive (via `children`),
// which can cause react-hook-form's `Path` type computation to explode/cycle.
// We only need the `value` slot for reads/writes in this component, so we keep
// the form-state type shallow and cast to `NormalizedFormField` at the boundaries.
type FieldValue = string | boolean | AddressDTO | string[] | null | undefined;
type FormDynamicField = { value?: FieldValue } & object;
type FormFieldValue = FormDynamicField | string | boolean | undefined;
type OverlayElement = 'button' | 'div';

function isFormDynamicField(field: FormFieldValue): field is FormDynamicField {
  return typeof field === 'object' && field !== null && 'value' in field;
}

export type FormFieldsData = {
  signature?: string;
  signatureDraw?: string;
  signatureTyped?: string;
  initial?: string;
  initialDraw?: string;
  initialTyped?: string;
  consent?: boolean;
  startSigning?: boolean;
} & Record<string, FormFieldValue>;

function getRawFieldValue(
  formValues: FormFieldsData,
  fieldStateKey: string,
) {
  return get(formValues, [fieldStateKey, 'value']);
}

function getSelectedCheckboxGroupChildIds(rawValue: unknown): string[] {
  return !isArray(rawValue)
    ? []
    : rawValue.filter((childId) => !isEmptyString(childId));
}

function getFieldDisplayValue(
  field: AnvilForm,
  formValues: FormFieldsData,
): string {
  const rawValue = getRawFieldValue(formValues, getFieldStateKey(field));

  if (field.type === 'usAddress') {
    return getAddressString(rawValue as AddressDTO, '');
  }

  if (field.type === 'checkbox') {
    return rawValue === true ? '✔' : '';
  }

  const format = formatterByType[field.type] ?? ((val: string) => val);
  return format((rawValue as string) ?? '');
}

function getOverlayStyle(
  field: AnvilForm,
  pageHeight: number,
  scaleFactor: number,
  options?: { isSignField?: boolean; fontSize?: number | null },
): CSSProperties {
  const isSignField = options?.isSignField ?? false;

  return {
    ...field?.rect
      ? getStyleFromRect(field, pageHeight, scaleFactor, isSignField)
      : {},
    justifyContent: getFieldHorizontalAlignment(field.horizontalAlignment),
    alignItems: getFieldVerticalAlignment(field.verticalAlignment),
    textAlign: getFieldTextAlignment(field.horizontalAlignment),
    fontSize: options?.fontSize ? `${options.fontSize}px` : undefined,
    zIndex: 100,
  };
}

function setFieldRef(
  fieldsRefs: { current: Record<string, HTMLElement> },
  fieldStateKey: string,
) {
  return (element: HTMLElement | null) => {
    if (element && fieldStateKey) {
      fieldsRefs.current[fieldStateKey] = element;
    }
  };
}

function getOverlayClassName(
  options: {
    isCurrentField: boolean;
    hasValidationError?: boolean;
    isClickable?: boolean;
    isCentered?: boolean;
    isRadioOption?: boolean;
  },
) {
  return clsx(
    'pdf-overlay-text flex',
    {
      'appearance-none cursor-pointer': options.isClickable,
      'whitespace-pre-wrap': !options.isRadioOption,
      'items-center': options.isRadioOption,
      'text-center': options.isCentered,
      '!border-2 !border-populate-green': options.isCurrentField,
      '!border-red-500 !bg-red-50': options.hasValidationError,
    },
  );
}

function getOverlayElement(isClickable: boolean): OverlayElement {
  return isClickable ? 'button' : 'div';
}

function getValidationMessage(field: AnvilForm) {
  switch (field.type) {
    case 'checkbox':
      return 'Please check this box to continue.';
    case 'radioGroup':
      return 'Please select an option.';
    case 'checkboxGroup':
      return 'Please select at least one option.';
    case 'email':
      return 'Please enter a valid email address.';
    case 'phone':
      return 'Please enter a valid phone number.';
    case 'date':
    case 'signatureDate':
      return 'Please enter a valid date.';
    case 'usAddress':
      return 'Please enter a valid address.';
    default:
      return 'This field is required.';
  }
}

function validateField(data: FormFieldValue, field: AnvilForm): string | null {
  if (!isFormFieldRequired(field)) {
    return null;
  }

  const value: FieldValue = isFormDynamicField(data) ? data.value : undefined;
  const validateFn = validatorsByType[field.type] ?? ((_value) => !isEmptyString(_value));
  return validateFn(value) ? null : getValidationMessage(field);
}

function validate(data: FormFieldValue, field: AnvilForm) {
  return isNil(validateField(data, field));
}

type Props = {
  data: NormalizedFormTemplateMetaData[];
  onSubmit?: (data: FormFieldsData) => void;
};

export default function AnvilFormFillable({ data, onSubmit }: Props) {
  useOrgTimeZone();

  const containerRef = useRef<HTMLDivElement>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const formProps = useForm<FormFieldsData>({
    mode: 'onChange',
    defaultValues: {
      consent: false,
      signature: '',
      signatureDraw: '',
      signatureTyped: '',
      initial: '',
      initialDraw: '',
      initialTyped: '',
    },
  });

  const {
    control,
    watch,
    reset,
    handleSubmit,
    setValue,
    getValues,
    setError: setFormError,
    clearErrors,
    formState: { submitCount, errors },
    trigger,
  } = formProps;
  const hasSignatureField = useMemo(() => hasFieldByType(data, 'signature'), [data]);
  const hasInitialField = useMemo(() => hasFieldByType(data, 'initial'), [data]);
  const hasNoSignFields = useMemo(
    () => !hasSignatureField && !hasInitialField,
    [hasSignatureField, hasInitialField],
  );
  const fieldsRefs = useRef<Record<string, HTMLElement>>({});
  const pendingFocusFieldKeyRef = useRef<string | null>(null);
  const [currentFieldIndex, setCurrentFieldIndex] = useState<number>(0);
  const [
    signature,
    consent,
    startSigning,
    initial,
    signatureDraw,
    signatureTyped,
    initialDraw,
    initialTyped,
  ] = watch([
    'signature',
    'consent',
    'startSigning',
    'initial',
    'signatureDraw',
    'signatureTyped',
    'initialDraw',
    'initialTyped',
  ] as const);
  const formValues = watch();

  const [isDocumentsLoaded, setIsDocumentsLoaded] = useState<boolean[]>([]);
  const [signatureTab, setSignatureTab] = useState<'draw' | 'type'>('type');
  const [initialTab, setInitialTab] = useState<'draw' | 'type'>('type');
  const [signatureDrawingData, setSignatureDrawingData] = useState<any>(null);
  const [initialDrawingData, setInitialDrawingData] = useState<any>(null);
  const signaturePadRef = useRef<SignatureCanvas | null>(null);
  const initialPadRef = useRef<SignatureCanvas | null>(null);
  const isSignatureDrawing = signatureTab === 'draw';
  const isSignatureTyping = signatureTab === 'type';
  const isInitialDrawing = initialTab === 'draw';
  const isInitialTyping = initialTab === 'type';

  const handleLoadSuccess = (index: number) => {
    setIsDocumentsLoaded((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
  };

  const fields = useMemo(
    () => (isDocumentsLoaded?.length > 0
      ? flatMap(data, 'fields') as AnvilForm[] : []),
    [isDocumentsLoaded, data],
  );

  useEffect(() => {
    if (!isNil(fields)) {
      const preserved = getPreservedGlobalPrepValues(getValues());
      const orgNow = getOrgNow();
      const fieldDefaults = fields.map((field) => {
        if (field.type === 'signature' && !isEmptyString(preserved.signature)) {
          return { ...field, value: preserved.signature };
        }
        if (field.type === 'initial' && !isEmptyString(preserved.initial)) {
          return { ...field, value: preserved.initial };
        }
        if (field.type === 'signatureDate' && isEmptyString(field.value)) {
          return { ...field, value: formatDate(orgNow.toDate()) };
        }
        if (field.type === 'signatureTimestamp' && isEmptyString(field.value)) {
          return { ...field, value: formatSignatureTimestamp(orgNow) };
        }
        if (field.type === 'checkboxGroup' && !isArray(field.value)) {
          return { ...field, value: [] };
        }
        return field;
      });

      reset({
        ...preserved,
        ...keyBy(fieldDefaults, (field) => getFieldStateKey(field)),
      });
    }
  }, [fields, reset]);

  const isLastIndex = useMemo(
    () => (fields.length - 1) === currentFieldIndex,
    [fields, currentFieldIndex],
  );

  const handleNextButton = () => {
    setCurrentFieldIndex((prev) => ((fields.length - 1) > prev ? prev + 1 : prev));
  };

  const submit = (data: FormFieldsData) => {
    if (!isLastIndex) {
      handleNextButton();
    } else {
      const updated = { ...data };
      fields.forEach((field) => {
        if (field?.type === 'usAddress') {
          const fieldStateKey = getFieldStateKey(field);
          const raw = data[fieldStateKey];
          const parts = (isFormDynamicField(raw)
            && typeof raw.value === 'object'
            && !isNil(raw.value)
            && !isArray(raw.value))
            ? raw.value
            : undefined;
          const composed = getAddressString(parts as AddressDTO);
          const prev = updated[fieldStateKey];
          if (isFormDynamicField(prev)) {
            updated[fieldStateKey] = { ...prev, value: composed };
          }
        }
      });
      onSubmit?.(updated);
    }
  };

  const canProceedToSigning = useMemo(
    () => Boolean(consent)
      && (!hasSignatureField || !isEmptyString(signature))
      && (!hasInitialField || !isEmptyString(initial)),
    [consent, hasSignatureField, signature, hasInitialField, initial],
  );

  const disableSigning = Boolean(startSigning);

  const isFormFillable = useMemo(
    () => canProceedToSigning && Boolean(startSigning),
    [canProceedToSigning, startSigning],
  );

  useEffect(() => {
    const fieldStateKey = getFieldStateKey(fields[currentFieldIndex] ?? {});
    const currentRef = fieldsRefs.current[fieldStateKey];
    if (currentRef && isFormFillable) {
      currentRef.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      if (pendingFocusFieldKeyRef.current === fieldStateKey) {
        window.requestAnimationFrame(() => {
          const focusTarget = document.getElementById(fields[currentFieldIndex]?.id ?? '');
          if (focusTarget instanceof HTMLElement) {
            focusTarget.focus();
          } else {
            currentRef.focus?.();
          }
          pendingFocusFieldKeyRef.current = null;
        });
      }
    }
  }, [currentFieldIndex, fields, isFormFillable]);

  const handlePdfFieldActivate = (fieldStateKey: string) => {
    const nextFieldIndex = fields.findIndex((field) => getFieldStateKey(field) === fieldStateKey);

    if (nextFieldIndex >= 0) {
      setCurrentFieldIndex(nextFieldIndex);
    }
  };

  const syncFieldValidationState = (
    field: AnvilForm,
    fieldData?: FormFieldValue,
  ) => {
    const fieldStateKey = getFieldStateKey(field);
    const message = validateField(
      fieldData ?? getValues(fieldStateKey),
      field,
    );

    if (isNil(message)) {
      clearErrors(fieldStateKey);
      return true;
    }

    setFormError(fieldStateKey, {
      type: 'manual',
      message,
    });
    return false;
  };

  const focusFieldByKey = (fieldStateKey: string) => {
    const nextFieldIndex = fields.findIndex((field) => getFieldStateKey(field) === fieldStateKey);

    if (nextFieldIndex >= 0) {
      pendingFocusFieldKeyRef.current = fieldStateKey;
      setCurrentFieldIndex(nextFieldIndex);
    }
  };

  const validateAllFields = (formData: FormFieldsData) => fields.reduce<Array<{
    field: AnvilForm;
    fieldStateKey: string;
  }>>((acc, field) => {
    const fieldStateKey = getFieldStateKey(field);
    const message = validateField(formData[fieldStateKey], field);

    if (isNil(message)) {
      clearErrors(fieldStateKey);
      return acc;
    }

    setFormError(fieldStateKey, {
      type: 'manual',
      message,
    });
    acc.push({ field, fieldStateKey });
    return acc;
  }, []);

  const validateCurrentField = () => {
    const activeField = fields[currentFieldIndex];
    const fieldStateKey = getFieldStateKey(activeField ?? {});

    if (activeField && !isNil(activeField.id)) {
      return syncFieldValidationState(activeField, getValues(fieldStateKey));
    }
    return true;
  };

  const handleContinue = () => {
    if (validateCurrentField()) {
      submit(getValues());
    } else if (fields[currentFieldIndex]) {
      focusFieldByKey(getFieldStateKey(fields[currentFieldIndex] ?? {}));
    }
  };

  const handleSubmitAttempt = (formData: FormFieldsData) => {
    if (isLastIndex) {
      const invalidFields = validateAllFields(formData);

      if (invalidFields.length > 0) {
        focusFieldByKey(invalidFields[0].fieldStateKey);
      } else {
        submit(formData);
      }
    } else {
      handleContinue();
    }
  };

  const handleInvalidSubmit = () => {
    if (isLastIndex) {
      const invalidFields = validateAllFields(getValues());

      if (invalidFields.length > 0) {
        focusFieldByKey(invalidFields[0].fieldStateKey);
      }
    } else {
      handleContinue();
    }
  };

  const handlePdfFieldValueChange = (fieldStateKey: string, value: boolean | string | string[]) => {
    const previousValue = getValues(fieldStateKey);
    const fieldValue: Record<string, unknown> = isPlainObject(previousValue)
      ? previousValue as Record<string, unknown>
      : {};
    const nextFieldData = {
      ...fieldValue,
      value,
    };

    setValue(
      fieldStateKey,
      nextFieldData,
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      },
    );

    const matchedField = fields.find((item) => getFieldStateKey(item) === fieldStateKey);
    if (matchedField) {
      syncFieldValidationState(matchedField, nextFieldData);
    }
  };

  const currentField = useMemo(
    () => fields[currentFieldIndex] as AnvilForm,
    [currentFieldIndex, isFormFillable],
  );

  const Component = useMemo(() => componentMap[currentField?.type as FieldType], [currentField]);

  const filteredConsentFields = useMemo(
    () => signPreparationFields
      .filter((field) => {
        if (field.name === 'signature') {
          return hasSignatureField;
        }
        if (field.name === 'initial') {
          return hasInitialField;
        }
        if (field.name === 'consent') {
          return !hasNoSignFields;
        }
        if (field.name === 'consentWithoutSigning') {
          return hasNoSignFields;
        }
        return true;
      })
      .map((field) => (field.name === 'consentWithoutSigning'
        ? {
          ...field,
          name: 'consent',
        }
        : field)),
    [hasSignatureField, hasInitialField, hasNoSignFields],
  );

  const consentField = useMemo(
    () => filteredConsentFields.find((field) => field.name === 'consent'),
    [filteredConsentFields],
  );

  const updateContainerWidth = () => {
    if (!isNil(containerRef.current)) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  };

  useEffect(() => {
    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);

  const signatureBoxRef = useRef<HTMLDivElement | null>(null);
  const [signatureCanvasWidth, setSignatureCanvasWidth] = useState<number>(360);
  const [signatureInteracted, setSignatureInteracted] = useState<boolean>(false);
  const signatureCanvasHeight = useMemo(
    () => (signatureCanvasWidth < 320 ? 80 : 120),
    [signatureCanvasWidth],
  );

  const initialBoxRef = useRef<HTMLDivElement | null>(null);
  const [initialCanvasWidth, setInitialCanvasWidth] = useState<number>(360);
  const [initialInteracted, setInitialInteracted] = useState<boolean>(false);
  const initialCanvasHeight = useMemo(
    () => (initialCanvasWidth < 320 ? 80 : 120),
    [initialCanvasWidth],
  );

  const updateCanvasWidth = () => {
    const el = signatureBoxRef.current;
    if (!isNil(el)) {
      const boxWidth = el.clientWidth ?? 0;
      if (boxWidth >= 0) {
        const maxPreferred = 420;
        const minWidth = 180;
        const target = Math.min(boxWidth, maxPreferred);
        setSignatureCanvasWidth(Math.max(minWidth, target));
      }
    }
  };

  const updateInitialCanvasWidth = () => {
    const el = initialBoxRef.current;
    if (!isNil(el)) {
      const boxWidth = el.clientWidth ?? 0;
      if (boxWidth >= 0) {
        const maxPreferred = 420;
        const minWidth = 180;
        const target = Math.min(boxWidth, maxPreferred);
        setInitialCanvasWidth(Math.max(minWidth, target));
      }
    }
  };

  useLayoutEffect(() => {
    // When switching to "Draw", the element may not have a width on the first tick.
    // Measure on layout and retry once on the next frame.
    updateCanvasWidth();
    const raf = window.requestAnimationFrame(() => {
      updateCanvasWidth();
    });
    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [signatureTab]);

  useEffect(() => {
    const onResize = () => updateCanvasWidth();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    // Make the canvas responsive to container changes (mobile/orientation/layout shifts)
    let ro: ResizeObserver | undefined;
    const el = signatureBoxRef.current;
    if (!isNil(el) && !isNil(ResizeObserver)) {
      ro = new ResizeObserver(() => { updateCanvasWidth(); });
      ro.observe(el);
    }
    return () => { ro?.disconnect(); };
  }, [signatureTab]);

  // Initial canvas resize handlers
  useLayoutEffect(() => {
    updateInitialCanvasWidth();
    const raf = window.requestAnimationFrame(() => {
      updateInitialCanvasWidth();
    });
    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [initialTab]);

  useEffect(() => {
    const onResize = () => updateInitialCanvasWidth();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    let ro: ResizeObserver | undefined;
    const el = initialBoxRef.current;
    if (!isNil(el) && !isNil(ResizeObserver)) {
      ro = new ResizeObserver(() => { updateInitialCanvasWidth(); });
      ro.observe(el);
    }
    return () => { ro?.disconnect(); };
  }, [initialTab]);

  const hasInitializedSignatureSync = useRef(false);

  useEffect(() => {
    const selected = isSignatureDrawing ? (signatureDraw ?? '') : (signatureTyped ?? '');
    if (!hasInitializedSignatureSync.current) {
      hasInitializedSignatureSync.current = true;
      setValue('signature', selected, { shouldValidate: false });
    } else {
      setValue('signature', selected, {
        shouldValidate: false,
        shouldTouch: false,
        shouldDirty: false,
      });
    }
  }, [signatureTab, signatureDraw, signatureTyped]);

  const hasInitializedInitialSync = useRef(false);

  useEffect(() => {
    const selected = isInitialDrawing ? (initialDraw ?? '') : (initialTyped ?? '');
    if (!hasInitializedInitialSync.current) {
      hasInitializedInitialSync.current = true;
      setValue('initial', selected, { shouldValidate: false });
    } else {
      setValue('initial', selected, {
        shouldValidate: false,
        shouldTouch: false,
        shouldDirty: false,
      });
    }
  }, [initialTab, initialDraw, initialTyped]);

  useEffect(() => {
    if (!startSigning || fields.length === 0) {
      return;
    }

    applyGlobalSignatureToFields(
      fields,
      signature ?? '',
      initial ?? '',
      setValue,
      getValues,
    );
  }, [fields, signature, initial, startSigning, setValue, getValues]);

  useEffect(() => {
    document.documentElement.classList.add('lock-scroll');
    document.body.classList.add('lock-scroll');

    return () => {
      document.documentElement.classList.remove('lock-scroll');
      document.body.classList.remove('lock-scroll');
    };
  }, []);

  return (
    <div className={clsx('m-auto max-w-[870px] shadow-md')} ref={containerRef}>
      {!isEmptyString(loadError) && (
        <div className="flex flex-1 justify-center items-center h-screen">
          <p className="error-message text-center">
            {loadError}
          </p>
        </div>
      )}
      {data.length !== isDocumentsLoaded.length && isEmptyString(loadError) && (
        <div className="absolute left-0 right-0 bg-white h-screen z-10">
          <Spinner
            name="FadeLoader"
            className="items-center h-full"
          />
        </div>
      )}
      <FormProvider {...formProps}>
        <div className="h-0">
          <div
            className={clsx(
              'absolute top-0 left-0 right-0 z-[999] flex justify-center w-full bg-white shadow-md',
              { 'opacity-100 pointer-events-auto py-4': isFormFillable },
              { 'opacity-0 pointer-events-none py-0 h-0': !isFormFillable },
              animationClass,
            )}
          >
            {isFormFillable && currentField && Component && (
              <div className="flex flex-col gap-4 px-4 w-full md:w-2/3 lg:w-1/2">
                <Controller
                  key={getFieldStateKey(currentField)}
                  name={getFieldStateKey(currentField)}
                  rules={{
                    required: isFormFieldRequired(currentField),
                    validate: (fieldData) => validate(fieldData, currentField),
                  }}
                  control={control}
                  render={({
                    field: {
                      ref: _ref,
                      value,
                      onChange,
                      ...field
                    },
                    fieldState: { invalid, error },
                  }) => (
                    <Wrapper
                      error={invalid}
                      errorText={error?.message}
                      id={currentField?.id ?? ''}
                      label={(
                        <FormFieldWizardLabel
                          field={currentField}
                          id={currentField?.id ?? ''}
                        />
                      )}
                      containerClassName="!cursor-default"
                    >
                      <Component
                        {...field}
                        error={invalid}
                        onChange={(nextValue) => {
                          onChange(nextValue);
                          syncFieldValidationState(currentField, nextValue);
                        }}
                        value={value as AnvilForm}
                        field={currentField}
                      />
                    </Wrapper>
                  )}
                />
                <div className="flex flex-row justify-between w-full">
                  <Button
                    variant="contained"
                    className="w-32 h-9 !py-0 !capitalize !bg-populate-green hover:!bg-populate-light-green"
                    classWrap="!w-fit"
                    onClick={isLastIndex
                      ? handleSubmit(handleSubmitAttempt, handleInvalidSubmit)
                      : handleContinue}
                    type="submit"
                  >
                    {isLastIndex ? 'Submit' : 'Continue'}
                  </Button>
                  <div className="flex flex-row items-center gap-2">
                    <span>
                      Step
                      {' '}
                      {currentFieldIndex + 1}
                      {' '}
                      of
                      {' '}
                      {fields.length}
                      {' '}
                    </span>
                    <div>
                      <IconButton
                        disabled={currentFieldIndex === 0}
                        onClick={() => {
                          if (currentFieldIndex > 0) {
                            setCurrentFieldIndex((prev) => prev - 1);
                          }
                        }}
                        className="!p-0"
                      >
                        <ChevronLeftIcon />
                      </IconButton>
                      <IconButton
                        disabled={fields.length === currentFieldIndex + 1}
                        onClick={() => {
                          if (validateCurrentField()) {
                            handleNextButton();
                          } else {
                            focusFieldByKey(getFieldStateKey(fields[currentFieldIndex] ?? {}));
                          }
                        }}
                        className="!p-0"
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="overflow-y-auto h-screen">
          <div className={clsx('bg-white mb-4', { hidden: !isEmptyString(loadError) })}>
            <div className="flex flex-col gap-8 max-w-2xl m-auto p-8 w-full">
              {!isNil(filteredConsentFields) && filteredConsentFields?.map((item, index) => (
                <Controller
                  key={getStableKey(item)}
                  name={item.name}
                  rules={{ required: true }}
                  control={control}
                  render={({ field, fieldState }) => {
                    const { invalid } = fieldState;
                    const isConsent = item.name === 'consent';
                    const isConsentChecked = (isConsent && typeof field.value === 'boolean') ? field.value : false;
                    const completed = isConsent ? isConsentChecked : !isEmptyString(field.value);
                    const showError = invalid && (fieldState.isTouched || submitCount > 0);
                    const showSignatureError = invalid && (signatureInteracted || submitCount > 0);
                    return (
                      <Wrapper
                        error={item.name === 'signature' ? showSignatureError : showError}
                        id={item.name}
                        showErrorText={false}
                        containerClassName="!cursor-default"
                      >
                        <div className="flex flex-col gap-2">
                          <SignatureStep
                            completed={completed}
                            stepNumber={index + 1}
                            title={item.title}
                            description={item?.description ?? ''}
                          />
                          {isConsent ? (
                            <div className="flex items-start gap-2 ml-10">
                              <MuiCheckbox
                                {...field}
                                checked={isConsentChecked}
                                onChange={(e) => {
                                  field.onChange(e.target.checked);
                                }}
                              />
                              <p className="text-sm text-gray-500">{consentField?.description}</p>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-4 sm:ml-12">
                              {item.name === 'signature' && (
                                <SignatureBox
                                  mode={signatureTab}
                                  onModeChange={setSignatureTab}
                                  canvasRef={signaturePadRef}
                                  boxRef={signatureBoxRef}
                                  canvasWidth={signatureCanvasWidth}
                                  canvasHeight={signatureCanvasHeight}
                                  typedValue={signatureTyped ?? ''}
                                  onDraw={(data) => {
                                    setValue('signatureDraw', data, { shouldValidate: true });
                                  }}
                                  onType={(value) => {
                                    setValue('signatureTyped', value, { shouldValidate: true });
                                  }}
                                  onClear={() => {
                                    if (isSignatureDrawing) {
                                      signaturePadRef.current?.clear();
                                      setValue('signatureDraw', '', { shouldValidate: true });
                                      setSignatureDrawingData(null);
                                    } else {
                                      setValue('signatureTyped', '', { shouldValidate: true });
                                    }
                                  }}
                                  setInteracted={setSignatureInteracted}
                                  isDisabled={disableSigning}
                                  placeholder={isSignatureTyping ? 'Type your signature...' : ''}
                                  inputId="signature-input"
                                  drawingData={signatureDrawingData}
                                  onSaveDrawingData={setSignatureDrawingData}
                                />
                              )}
                              {item.name === 'initial' && (
                                <SignatureBox
                                  mode={initialTab}
                                  onModeChange={setInitialTab}
                                  canvasRef={initialPadRef}
                                  boxRef={initialBoxRef}
                                  canvasWidth={initialCanvasWidth}
                                  canvasHeight={initialCanvasHeight}
                                  typedValue={initialTyped ?? ''}
                                  onDraw={(data) => {
                                    setValue('initialDraw', data, { shouldValidate: true });
                                  }}
                                  onType={(value) => {
                                    setValue('initialTyped', value, { shouldValidate: true });
                                  }}
                                  onClear={() => {
                                    if (isInitialDrawing) {
                                      initialPadRef.current?.clear();
                                      setValue('initialDraw', '', { shouldValidate: true });
                                      setInitialDrawingData(null);
                                    } else {
                                      setValue('initialTyped', '', { shouldValidate: true });
                                    }
                                  }}
                                  setInteracted={setInitialInteracted}
                                  isDisabled={disableSigning}
                                  placeholder={isInitialTyping ? 'Type your initials...' : ''}
                                  inputId="initial-input"
                                  drawingData={initialDrawingData}
                                  onSaveDrawingData={setInitialDrawingData}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </Wrapper>
                    );
                  }}
                />
              ))}
              {!isFormFillable && (
                <Controller
                  name="startSigning"
                  rules={{ required: true }}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Wrapper
                      error={!isNil(error)}
                      id="startSigning"
                      showErrorText={false}
                      containerClassName="!cursor-default justify-end"
                      className="justify-end"
                    >
                      <Button
                        classWrap="!w-auto"
                        variant="contained"
                        disabled={!canProceedToSigning}
                        onClick={() => { field.onChange(true); }}
                        className="capitalize !bg-populate-green hover:!bg-populate-light-green"
                      >
                        {hasNoSignFields ? 'Start Filling' : 'Start Signing'}
                      </Button>
                    </Wrapper>
                  )}
                />
              )}
            </div>
          </div>
          {data?.map((file, index) => {
            const pageWidth = head(file.pages)?.width ?? 0;
            const pageHeight = head(file.pages)?.height ?? 0;
            const scaleFactor = containerWidth / pageWidth;
            const renderingHeight = pageHeight * scaleFactor;
            const fileNameClass = `w-${pageWidth}`;
            const fileName = head(file?.formPdf?.list)?.fileName;
            const pdfMedia = head(file?.formPdf?.list);
            const pdfFile = pdfMedia?.dataUrl ?? pdfMedia?.url?.value ?? null;
            const fileKey = getStableKey(file);

            return (
              <div key={fileKey} className="relative">
                <Document
                  file={pdfFile}
                  onLoadSuccess={() => { handleLoadSuccess(index); }}
                  onError={(error) => {
                    setLoadError(error?.message ?? 'Error loading PDF');
                  }}
                  onLoadError={(error) => {
                    setLoadError(error?.message ?? 'Error loading PDF');
                  }}
                >
                  {file?.pages?.map((page) => {
                    const pageKey = `${fileKey}-page-${page?.pageNumber ?? `${page?.width ?? ''}x${page?.height ?? ''}`}`;
                    const currentPageNumber = (page?.pageNumber ?? 0) + 1;
                    return (
                      <div key={pageKey} className="mb-2">
                        <ToolTip title={fileName}>
                          <div className={clsx('truncate font-medium text-md px-2', { [fileNameClass]: pageWidth })}>
                            {fileName}
                          </div>
                        </ToolTip>
                        <Page
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          pageNumber={currentPageNumber}
                          height={renderingHeight}
                          width={pageWidth * (renderingHeight / pageHeight)}
                          className="mb-2"
                          onRenderSuccess={(e) => { }}
                        />
                        <div className="flex justify-end text-xs text-gray-600 py-1 px-2">
                          <div>
                            {' '}
                            Page
                            {' '}
                            {currentPageNumber}
                            {' '}
                            of
                            {' '}
                            {file?.pages?.length}
                          </div>
                        </div>
                        {/* Overlay all fields on their respective pages */}
                        {file?.fields.map((field) => {
                          const fieldStateKey = getFieldStateKey(field);
                          const hasValidationError = !isNil(get(errors, fieldStateKey));
                          const isCurrentField = field.id === currentField?.id
                            && field.formId === currentField?.formId
                            && isFormFillable;
                          const rawValue = getRawFieldValue(formValues, fieldStateKey);
                          const value = getFieldDisplayValue(field, formValues);
                          const isIconText = ['radioGroup', 'checkbox'].includes(field?.type);
                          const isSignField = ['signature', 'initial'].includes(field?.type);
                          const isSignatureImage = isSignField
                          && typeof value === 'string'
                          && (value as string).startsWith('data:image');
                          const fieldKey = getFieldStateKey(field);
                          const selectedCheckboxGroupChildIds = field.type === 'checkboxGroup'
                            ? getSelectedCheckboxGroupChildIds(rawValue)
                            : [];
                          const isCheckboxFieldClickable = isFormFillable
                            && field.type === 'checkbox'
                            && Boolean(field.id);
                          const FieldOverlay = getOverlayElement(isCheckboxFieldClickable);

                          if (Number(field.pageNum) >= 0) {
                            return (
                              <div key={fieldKey}>
                                {field.type !== 'radioGroup' && field.type !== 'checkboxGroup' && (
                                  <FieldOverlay
                                    {...(isCheckboxFieldClickable ? { type: 'button' as const } : {})}
                                    ref={setFieldRef(fieldsRefs, fieldStateKey)}
                                    style={getOverlayStyle(field, pageHeight, scaleFactor, {
                                      isSignField,
                                      fontSize: field.fontSize,
                                    })}
                                    className={getOverlayClassName({
                                      isCurrentField,
                                      hasValidationError,
                                      isClickable: isCheckboxFieldClickable,
                                      isCentered: isIconText,
                                    })}
                                    data-field-state-key={fieldStateKey}
                                    data-invalid={hasValidationError || undefined}
                                    aria-invalid={hasValidationError}
                                    onClick={isCheckboxFieldClickable
                                      ? () => {
                                        handlePdfFieldActivate(fieldStateKey);
                                        handlePdfFieldValueChange(fieldStateKey, rawValue !== true);
                                      }
                                      : undefined}
                                  >
                                    {!isIconText && (
                                      isSignatureImage
                                        ? (
                                          <img
                                            src={value}
                                            alt="signature"
                                            className="h-full object-contain signature-color dancing-script-regular"
                                          />
                                        )
                                        : (
                                          <span className={clsx({ 'dancing-script-regular signature-color': isSignField })}>
                                            {value}
                                          </span>
                                        )
                                    )}
                                    {field?.type === 'checkbox' ? (
                                      <span className="pdf-overlay-check-mark">{value}</span>
                                    ) : null}
                                  </FieldOverlay>
                                )}
                                {/* Handle radio groups */}
                                {field.type === 'radioGroup' && (field?.children?.length ?? 0) > 0 && field?.children?.map((child) => {
                                  const isRadioFieldClickable = isFormFillable
                                    && Boolean(field.id)
                                    && Boolean(child?.id);
                                  const RadioOverlay = getOverlayElement(isRadioFieldClickable);

                                  return (
                                    <RadioOverlay
                                      key={getStableKey(child)}
                                      {...(isRadioFieldClickable ? { type: 'button' as const } : {})}
                                      ref={setFieldRef(fieldsRefs, fieldStateKey)}
                                      style={getOverlayStyle(
                                        child as AnvilForm,
                                        pageHeight,
                                        scaleFactor,
                                      )}
                                      className={getOverlayClassName({
                                        isCurrentField,
                                        hasValidationError,
                                        isClickable: isRadioFieldClickable,
                                        isRadioOption: true,
                                      })}
                                      data-field-state-key={fieldStateKey}
                                      data-invalid={hasValidationError || undefined}
                                      aria-invalid={hasValidationError}
                                      onClick={isRadioFieldClickable
                                        ? () => {
                                          handlePdfFieldActivate(fieldStateKey);
                                          handlePdfFieldValueChange(fieldStateKey, child.id ?? '');
                                        }
                                        : undefined}
                                    >
                                      {value === child?.id ? (
                                        <span className="pdf-overlay-check-mark">✔</span>
                                      ) : null}
                                    </RadioOverlay>
                                  );
                                })}
                                {field.type === 'checkboxGroup' && (field?.children?.length ?? 0) > 0
                                  && field?.children?.map((child) => {
                                    const isCheckboxGroupOptionClickable = isFormFillable
                                    && !isEmptyString(field.id ?? '')
                                    && !isEmptyString(child?.id ?? '');
                                    const CheckboxGroupOverlay = getOverlayElement(
                                      isCheckboxGroupOptionClickable,
                                    );
                                    const childId = child?.id ?? '';
                                    const isChildSelected = selectedCheckboxGroupChildIds
                                      .includes(childId);

                                    return (
                                      <CheckboxGroupOverlay
                                        key={getStableKey(child)}
                                        {...(isCheckboxGroupOptionClickable ? { type: 'button' as const } : {})}
                                        ref={setFieldRef(fieldsRefs, fieldStateKey)}
                                        style={getOverlayStyle(
                                        child as AnvilForm,
                                        pageHeight,
                                        scaleFactor,
                                        )}
                                        className={getOverlayClassName({
                                          isCurrentField,
                                          hasValidationError,
                                          isClickable: isCheckboxGroupOptionClickable,
                                          isRadioOption: true,
                                        })}
                                        data-field-state-key={fieldStateKey}
                                        data-invalid={hasValidationError || undefined}
                                        aria-invalid={hasValidationError}
                                        onClick={() => {
                                          if (!isCheckboxGroupOptionClickable) {
                                            return;
                                          }

                                          const nextSelectedChildIds = isChildSelected
                                            ? selectedCheckboxGroupChildIds.filter(
                                              (selectedChildId) => selectedChildId !== childId,
                                            )
                                            : [...selectedCheckboxGroupChildIds, childId];
                                          handlePdfFieldActivate(fieldStateKey);
                                          handlePdfFieldValueChange(
                                            fieldStateKey,
                                            nextSelectedChildIds,
                                          );
                                        }}
                                      >
                                        {isChildSelected ? (
                                          <span className="pdf-overlay-check-mark">✔</span>
                                        ) : null}
                                      </CheckboxGroupOverlay>
                                    );
                                  })}
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    );
                  })}
                </Document>
              </div>
            );
          })}
        </div>
      </FormProvider>
    </div>
  );
}
